/* eslint no-restricted-syntax: 0 */
/* eslint no-await-in-loop: 0 */
/* eslint no-else-return: 0 */
/* eslint no-param-reassign: 0 */
import React from 'react';
import * as tf from '@tensorflow/tfjs';
import * as speechCommands from '@tensorflow-models/speech-commands';
import { MODEL_TYPES, STATE } from '../params';
import { fisherYatesWithTarget, plotSpectrogram, resampleTo16kHz, trimOrPadTo1Sec, getUID, uint8ToString } from '../util';


export default class SoundsHandler {
  modelType = MODEL_TYPES.SOUNDS;

  importModelApi = STATE.importModelApi;

  listeningInterval = 1000;

  numAudioFrames = 3;

  minSamplesRequired = {
    samples: 8,
    classes: 2,
  };

  trainingParams = {
    denseUnits: 100,
    epochs: 70,
    fineTunningEpochs: 20,
    learningRate: -1,
    batchSize: 32,
  };

  wrapper = null;

  classifying = false;

  predictionCallback = null;

  model = null;

  samples = {
    class0: {
      index: 0,
      uniqueName: speechCommands.BACKGROUND_NOISE_TAG,
      name: 'machineLearning.mlPanel.backgroundNoiseClass',
      nonEditable: true,
      image: [],
      active: true,
      tensors: [],
    },
    class1: {
      index: 1,
      uniqueName: getUID(),
      name: '',
      image: [],
      active: true,
      tensors: [],
    },
  };

  classNamesToIndex = {};

  constructor(wrapper, predictionCallback) {
    this.wrapper = wrapper;
    this.predictionCallback = predictionCallback;
  }

  init = async () => {
    console.log('Handling model Sounds');
    await this.initDefaultValueMap();
  }

  initDefaultValueMap = async () => {
    await tf.ready()
    const speechModel = speechCommands.create('BROWSER_FFT');
    await speechModel.ensureModelLoaded();

    this.model = speechModel.createTransfer('modelo-educabot-sonidos');
  }

  classify = async () => {
    if (this.model && this.predictionCallback) {
      const results = {};

      this.model.listen(
        async (result) => {
          if (result.spectrogram) {
            const { data, frameSize } = result.spectrogram

            plotSpectrogram(
              data,
              frameSize,
              frameSize,
              {
                pixelsPerFrame: 10,
              },
              this.wrapper || null,
            )
          }

          const words = this.model.wordLabels();

          for (let i = 0; i < result.scores.length; i += 1) {
            Object.keys(this.samples).forEach((key) => {
              if (words[i] === this.samples[key].uniqueName) {
                results[this.samples[key].name] = parseFloat(result.scores[i].toFixed(4))
              }
            });
          }

          await Promise.resolve(this.predictionCallback({ ...results }))
        },
        {
          // overlapFactor: 0.01,
          includeSpectrogram: true,
          // suppressionTimeMillis: 1000,
          // probabilityThreshold: 0,
          invokeCallbackOnNoiseAndUnknown: true,
        },
      );
    }
  }

  startMedia = async () => {
  }

  stopMedia = () => {
  }

  clearAllSamples = () => {
    this.classifying = false;
    this.samples = {};
    this.init();
    this.updateModelMetadata();
  }

  startClassifying = () => {
    this.classifying = true;
    this.classify();
  }

  stopClassifying = () => {
    this.classifying = false;
    if (this.model?.isListening()) {
      this.model.stopListening();
    }
  }

  setTrainingParams = (params) => {
    this.trainingParams = {
      ...this.trainingParams,
      ...params,
    };
  }

  getTrainingParams = () => {
    return this.trainingParams;
  }

  getMinSamplesRequired = () => {
    return this.minSamplesRequired;
  }

  addClass = (className = '') => {
    const classIndex = Object.keys(this.samples).length || 0;
    if (!Object.values(this.samples).some(sample => sample.name === className)) {
      this.samples[`class${classIndex}`] = {
        index: classIndex,
        uniqueName: getUID(),
        name: className,
        image: [],
        active: true,
        tensors: [],
      }
      this.updateModelMetadata()
    }
  }

  editClassName = (id, name) => {
    if (this.samples[id]) {
      this.samples[id].name = name
      this.updateModelMetadata()
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  deleteClass = (id) => {
    if (this.samples[id]) {
      const speechTensorToRemove = this.getSamples(true)[id]?.tensors || [];
      for (let i = 0; i < speechTensorToRemove.length; i += 1) {
        this.model.removeExample(speechTensorToRemove[i].uid)
      }

      delete this.samples[id];

      let i = 0;
      const newSamples = {};
      Object.keys(this.samples).map((sample) => {
        this.samples[sample].index = i;
        newSamples[`class${i}`] = this.samples[sample];
        i += 1;
        return null;
      });
      this.samples = { ...newSamples }
      this.updateModelMetadata();
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  deleteSampleImages = (id, indexes) => {
    if (this.model && this.samples[id]) {
      const speechTensorToRemove = this.getSamples(true)[id]?.tensors || [];
      if (speechTensorToRemove) {
        for (let i = 0; i < speechTensorToRemove.length; i += 1) {
          if (indexes.includes(i)) {
            this.model.removeExample(speechTensorToRemove[i].uid)
          }
        }
      }
      this.samples[id].image = this.samples[id].image.filter((_, index) => !indexes.includes(index))
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  getImageFromSpectrogram = (spectrogramData, frameSize) => {
    const canvas = document.createElement('canvas');
    plotSpectrogram(
      spectrogramData,
      frameSize,
      frameSize,
      {
        pixelsPerFrame: 7,
      },
      null,
      canvas,
    );

    return canvas.toDataURL('image/png');
  }

  addSample = async (className, _, audioBuffer) => {
    if (className && this.samples[className] && this.model) {
      const index = this.samples[className].index || 0
      const speechClassName = this.samples[className].uniqueName;

      if (audioBuffer) {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        const decoded = await audioCtx.decodeAudioData(audioBuffer.slice(0))
        const channelData = decoded.getChannelData(0)
        const resampled = this.resampleTo16kHz(channelData, decoded.sampleRate)
        const trimmed = this.trimOrPadTo1Sec(resampled)
        const audioTensor = tf.tensor1d(trimmed)
        const frameLength = 512
        const frameStep = 256
        const stft = tf.signal.stft(audioTensor, frameLength, frameStep)
        const spectrogramArray = stft.abs().arraySync()

        const croppedSpectrogramArray = spectrogramArray.map((frame) => frame.slice(0, 232))
        const flatData = Float32Array.from(croppedSpectrogramArray.flat())
        const frameSize = 232

        const expectedNumFrames = Math.floor(9976 / 232) // ≈ 43
        const actualNumFrames = Math.floor(flatData.length / 232)

        let resizedData
        if (actualNumFrames > expectedNumFrames) {
          resizedData = flatData.slice(0, expectedNumFrames * 232)
        } else if (actualNumFrames < expectedNumFrames) {
          resizedData = new Float32Array(expectedNumFrames * 232)
          resizedData.set(flatData)
        } else {
          resizedData = flatData
        }

        const spectrogramData = {
          data: resizedData,
          frameSize,
        }
        const example = {
          uid: getUID(),
          example: {
            label: speechClassName,
            rawAudio: undefined,
            spectrogram: spectrogramData,
          },
        }

        // Get an image base64 from the spectrogram data
        const imageBase64 = this.getImageFromSpectrogram(resizedData, frameSize)
        this.samples[className] = {
          ...this.samples[className],
          image: [...this.samples[className].image, imageBase64],
        }

        // Robust dataset update
        if (!this.model.dataset?.examples) this.model.dataset.examples = {}
        if (!this.model.dataset.label2Ids) this.model.dataset.label2Ids = {}

        this.model.dataset.examples[example.uid] = example.example

        if (!this.model.dataset?.label2Ids[speechClassName]) {
          this.model.dataset.label2Ids[speechClassName] = []
        }
        this.model.dataset.label2Ids[speechClassName].push(example.uid)

        if (!this.model.words) {
          this.model.words = []
        }
        if (!this.model.words.includes(speechClassName)) {
          this.model.words.push(speechClassName)
        }
      } else {
        let tempSpectrogramData;
        let tempSpectrogramFrameSize;
        const collectExampleOptions = {};
        collectExampleOptions.durationMultiplier = this.listeningInterval / 1000;
        collectExampleOptions.snippetDurationSec = 0.1;
        collectExampleOptions.onSnippet = async (spectrogram) => {
          if (tempSpectrogramData == null) {
            tempSpectrogramData = spectrogram.data;
          } else {
            tempSpectrogramData = speechCommands.utils.concatenateFloat32Arrays([
              tempSpectrogramData,
              spectrogram.data,
            ]);
          }
          tempSpectrogramFrameSize = spectrogram.frameSize;
          if (this.wrapper) {
            plotSpectrogram(
              tempSpectrogramData,
              tempSpectrogramFrameSize,
              tempSpectrogramFrameSize,
              {
                pixelsPerFrame: 7,
              },
              this.wrapper,
            );
          }
        };

        collectExampleOptions.includeRawAudio = false;

        await this.model.collectExample(speechClassName, collectExampleOptions);
        if (!this.model.words) {
          this.model.words = []
        }
        if (!this.model.words.includes(speechClassName)) {
          this.model.words.push(speechClassName)
        }

        // Take snapshot only after spectrogram is fully collected
        if (tempSpectrogramData) {
          const imageBase64 = this.getImageFromSpectrogram(tempSpectrogramData, tempSpectrogramFrameSize);
          this.samples[className] = {
            ...this.samples[className],
            image: [...this.samples[className].image, imageBase64],
          };
        }
      }

      return this.samples[className].image.length || 0
    }
    return 0
  }

  getSamples = (getTensors = false) => {
    return Object.keys(this.samples).reduce((acc, key) => {
      const speechClassName = this.samples[key].uniqueName;
      let examplesExist = null;
      if (getTensors) {
        examplesExist = this.model.dataset?.label2Ids?.[speechClassName]?.length > 0;
      }
      acc[key] = {
        ...this.samples[key],
        tensors: getTensors && examplesExist ? this.model.getExamples(speechClassName) : [],
      };
      return acc
    }, {})
  }

  importSamples = (samples) => {
    if (Object.keys(samples).length > 0) {
      this.samples = samples;
      const datasetSamples = {};
      const datasetLabel2Ids = {};
      const datasetWords = [];
      const DEFAULT_FRAME_DURATION = 23.22;

      Object.keys(this.samples).forEach((sampleKey) => {
        const sample = this.samples[sampleKey];
        const speechClassName = sample.uniqueName;
        sample.tensors.forEach((tensor) => {
          const example = tensor;
          if (example.example && example.example.spectrogram) {
            let data = example.example.spectrogram.data;
            if (Array.isArray(data) && data.length > 0) {
              // If it's a nested array, flatten it
              if (Array.isArray(data[0])) {
                data = Float32Array.from(data.flat());
              } else {
                data = Float32Array.from(data);
              }
              example.example.spectrogram.data = data;
            } else if (data instanceof Float32Array && data.length > 0) {
              // Already valid
              // do nothing
            } else if (typeof data === 'object' && data !== null && Object.keys(data).length > 0) {
              // Convert object with numeric keys to array
              const arr = Object.keys(data)
                .sort((a, b) => Number(a) - Number(b))
                .map(k => data[k]);
              example.example.spectrogram.data = Float32Array.from(arr);
            } else {
              // Invalid data, skip or set default
              console.warn('Invalid spectrogram data for tensor', typeof data, data);
              return; // skip this tensor
            }
            if (typeof example.example.spectrogram.frameDurationMillis === 'undefined') {
              example.example.spectrogram.frameDurationMillis = DEFAULT_FRAME_DURATION;
            }
          }
          if (!datasetSamples[tensor.uid]) {
            datasetSamples[tensor.uid] = {};
          }
          datasetSamples[tensor.uid] = example.example;

          if (!datasetLabel2Ids[speechClassName]) {
            datasetLabel2Ids[speechClassName] = []
          }
          datasetLabel2Ids[speechClassName].push(tensor.uid);

          if (!datasetWords.includes(speechClassName)) {
            datasetWords.push(speechClassName);
          }
        });
      });

      // Robust dataset update
      if (!this.model.dataset?.examples) this.model.dataset.examples = {}
      if (!this.model.dataset.label2Ids) this.model.dataset.label2Ids = {}

      this.model.dataset.examples = datasetSamples
      this.model.dataset.label2Ids = datasetLabel2Ids
      this.model.words = datasetWords
    }
  }

  train = async (callbacks) => {
    this.model.model = null;
    await tf.nextFrame();
    await this.model.train({
      epochs: parseInt(this.trainingParams.epochs, 10),
      batchSize: parseInt(this.trainingParams.batchSize, 10),
      validationSplit: 0.25,
      augmentByMixingNoiseRatio: 0.5, // null or 0.5 (there has to be a _background_noise_ class to use a number)
      callback: { ...callbacks, onTrainBegin: (logs) => callbacks.onTrainBegin(logs, 'Entrenamiento') },
      fineTuningEpochs: parseInt(this.trainingParams.fineTunningEpochs, 10),
      fineTuningCallback: { ...callbacks, onTrainBegin: (logs) => callbacks.onTrainBegin(logs, 'Refinamiento') },
    });

    // const callbacks = {};
    // callbacks.onTrainEnd = (logs) => {
    //   console.log('==========onTrainEnd', logs);
    // };
    // callbacks.onEpochBegin = (epoch, logs) => {
    //   console.log('==========onEpochBegin', epoch, logs);
    // };
    // callbacks.onEpochEnd = (epoch, logs) => {
    //   console.log('==========onEpochEnd', epoch, logs);
    // };

    return this.model;
  }

  saveModelToDB = async () => {
    // Save only model weights/topology/metadata (no samples)
    let modelJson = null;
    let modelWeights = null;

    const handlerToSave = async (artifacts) => {
      modelJson = {
        modelTopology: artifacts.modelTopology,
        weightsManifest: artifacts.weightsManifest,
        weightSpecs: artifacts.weightSpecs,
        format: artifacts.format,
        generatedBy: artifacts.generatedBy,
        convertedBy: artifacts.convertedBy,
        userDefinedMetadata: this.classNamesToIndex,
      };
      modelWeights = artifacts.weightData;
      return {
        modelArtifactsInfo: {
          dateSaved: new Date(),
          modelTopologyType: 'JSON',
          modelTopologyBytes: JSON.stringify(modelJson).length,
          weightSpecsBytes: artifacts.weightSpecs ? JSON.stringify(artifacts.weightSpecs).length : 0,
          weightDataBytes: modelWeights ? modelWeights.byteLength : 0,
        },
      };
    };

    await this.model.save(tf.io.withSaveHandler(handlerToSave));

    return {
      json: JSON.stringify(modelJson),
      weights: modelWeights,
      weightsBase64: modelWeights ? btoa(uint8ToString(new Uint8Array(modelWeights))) : null,
    };
  }

  loadModelFromDB = async (json, weights) => {
    // Load only model weights/topology/metadata (no samples)
    const modelData = JSON.parse(json);
    const weightData = atob(weights);
    const weightBuffer = Uint8Array.from(weightData, c => c.charCodeAt(0)).buffer;

    const modelArtifacts = {
      modelTopology: modelData.modelTopology,
      weightsManifest: modelData.weightsManifest,
      weightSpecs: modelData.weightSpecs,
      weightData: weightBuffer,
      format: modelData.format,
      generatedBy: modelData.generatedBy,
      convertedBy: modelData.convertedBy,
      userDefinedMetadata: modelData.userDefinedMetadata,
    };

    await this.initDefaultValueMap();
    if (this.model) {
      await this.model.load(tf.io.fromMemory(modelArtifacts));
      this.classNamesToIndex = this.model.model.userDefinedMetadata;
      return this.classNamesToIndex;
    } else {
      return false;
    }
  }

  loadModel = async (json, weights) => {
    try {
      await this.initDefaultValueMap();
      const modelArtifacts = await this.model.load(`${this.importModelApi}/${json}/${weights}/model.json`);

      if (this.model) {
        const words = this.model.wordLabels();
        this.model.words = [];
        this.classNamesToIndex = this.model.model.userDefinedMetadata;
        Object.keys(this.classNamesToIndex).forEach((className, index) => {
          const speechClassName = index === 0 ? speechCommands.BACKGROUND_NOISE_TAG : getUID();

          Object.keys(this.samples).forEach((defaultClassName, defaultIndex) => {
            if (this.samples[defaultClassName].index === index) {
              if (this.samples[defaultClassName].index === 0) {
                this.samples[defaultClassName].nonEditable = true;
              }
              this.samples[defaultClassName].active = true;
              this.samples[defaultClassName].name = className;
              this.samples[defaultClassName].uniqueName = speechClassName;
              this.samples[defaultClassName].image = [];
              this.samples[defaultClassName].tensors = [];
            }
          });
          if (index > Object.keys(this.samples).length - 1) {
            this.samples[`class${index}`] = {
              active: true,
              index,
              name: className,
              uniqueName: speechClassName,
              image: [],
              tensors: [],
            }
          }

          if (!this.model.words.includes(speechClassName)) {
            this.model.words.push(speechClassName)
          }
        });
        return this.classNamesToIndex;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  updateModelMetadata = () => {
    if (this.model) {
      this.classNamesToIndex = {};
      Object.keys(this.samples).forEach((key) => {
        const sample = this.samples[key];
        this.classNamesToIndex[sample.name] = sample.index;
      });
    }
  }
}
